/**
 * Sticky Block Builder Alpine.js Component
 *
 * Handles sticky positioning behavior for blocks with support for:
 * - Configurable sticky offset (pixels or element height-based)
 * - Device visibility controls
 * - Background changes on scroll
 * - Responsive layout options
 */
export default {
  name: 'stickyBlockBuilder',
  component(settings) {
    return {
      stickyOffset: 0,
      resizeObserver: null,
      renderTypeKey: 0, // Force reactivity for render type changes

      init() {
        // Wait for Alpine to mount, then calculate sticky offset
        this.$nextTick(() => {
          this.calculateStickyOffset();
          this.setupStickyOffsetObserver();
        });

        // Watch for screen size changes that might affect render type visibility
        let resizeTimeout;
        window.addEventListener('resize', () => {
          // Debounce resize events
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            // Recalculate sticky offset on resize
            this.calculateStickyOffset();
            // Force reactivity update for render type
            this.renderTypeKey++;
          }, 100);
        });

        this.setupReparentHeightCalculation();
      },

      // Calculate sticky offset based on settings and apply to parent
      calculateStickyOffset() {
        let offsetValue = 0;

        if (settings.sticky_offset_type === 'selector') {
          const targetElement = document.querySelector(settings.sticky_offset_selector);

          if (!targetElement) return;

          offsetValue = targetElement.offsetHeight;
        } else {
          // Fallback to pixel value
          offsetValue = settings.sticky_offset_px || 0;
        }

        this.stickyOffset = offsetValue;

        // Apply to parent element
        if (this.$el && this.$el.parentElement) {
          this.$el.parentElement.style.top = `${offsetValue}px`;
        }
      },

      // Setup resize observer for selector-based offset
      setupStickyOffsetObserver() {
        if (settings.sticky_offset_type === 'selector' && settings.sticky_offset_selector) {
          const targetElement = document.querySelector(settings.sticky_offset_selector);
          if (targetElement) {
            this.resizeObserver = new ResizeObserver(() => {
              this.calculateStickyOffset();
            });
            this.resizeObserver.observe(targetElement);
          }
        }
      },

      // Handle max height calculation for screen_minus_reparent type
      setupReparentHeightCalculation() {
        if (settings.max_height_type !== 'screen_minus_reparent' || !settings.sticky_offset_selector) {
          return;
        }

        const reparentElement = document.querySelector(settings.sticky_offset_selector);

        if (reparentElement) {
          const updateHeight = () => {
            const height = reparentElement.offsetHeight;
            this.$el.style.maxHeight = `calc(100dvh - ${height}px)`;
          };

          updateHeight();

          const resizeObserver = new ResizeObserver(updateHeight);
          resizeObserver.observe(reparentElement);

          this.reparentResizeObserver = resizeObserver;
        }
      }
    };
  }
};
